import React, { useEffect, useState } from "react";

import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from "@material-ui/core/styles";
import { Table, Typography } from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { HospitalSurvey } from "../types/hospital";
import { getServices } from "../api/hospital";
import { isOptionalQuestion, surveyCategoryFilter, surveyQuestionFilter } from "../util/form";
import { OptionalQuestion, Services } from "./Form/types";

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    body: {
      fontSize: 14,
      fontWeight: 500,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.secondary.light,
      },
    },
  })
)(TableRow);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    questionCategory: {
      color: theme.palette.secondary.main,
      background: theme.palette.info.main,
      fontWeight: 800,
      textTransform: "uppercase",
      [theme.breakpoints.only("xs")]: {
        display: "block",
      },
    },
    questionText: {
      fontWeight: 800,
      display: "block",
      [theme.breakpoints.up("md")]: {
        display: "table-cell",
        width: "30%",
      },
    },
    answerText: {
      textAlign: "left",
      display: "block",
      [theme.breakpoints.up("md")]: {
        display: "table-cell",
        width: "40%",
      },
    },
    notification: {
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
  })
);

type SurveyTableProps = {
  surveyData: HospitalSurvey;
  optionalQuestions: OptionalQuestion[];
};

export default function SurveyTable({ surveyData, optionalQuestions }: SurveyTableProps) {
  const classes = useStyles();
  const [services, setServices] = useState<Services>({ hasBirth: false, hasAbortion: false, hasMpo: false })

  useEffect(() => {
    async function findServicesFilters() {
      const responseData = await getServices(surveyData.hospitalName);
      setServices(responseData.data);
    }

    findServicesFilters();
  }, [surveyData])

  return (
    <TableContainer>
      {surveyData.previousAnswers && (
        <Typography color="primary" className={classes.notification}>
          U desnom stupcu nalaze se podaci koji su promijenjeni.
        </Typography>
      )}
      <Table aria-label="hospital survey">
        <TableBody>
          {surveyData &&
            surveyData.answers.filter((questionCategory, i) => surveyCategoryFilter(i, services.hasBirth, services.hasAbortion, services.hasMpo)).map((questionCategory, i) => (
              <React.Fragment key={i}>
                <StyledTableRow key={questionCategory.category}>
                  <StyledTableCell
                    scope="col"
                    colSpan={surveyData.previousAnswers ? 3 : 2}
                    className={classes.questionCategory}
                  >
                    {questionCategory.category}
                  </StyledTableCell>
                </StyledTableRow>
                {questionCategory.surveyAnswers
                  .filter(surveyAnswer => !(isOptionalQuestion(surveyAnswer.questionId) && surveyQuestionFilter(optionalQuestions, surveyAnswer.questionId, questionCategory.surveyAnswers)))
                  .map((surveyAnswer, j) => (
                  <StyledTableRow key={"question" + surveyAnswer.questionId}>
                    <StyledTableCell
                      scope="row"
                      className={classes.questionText}
                    >
                      {surveyAnswer.questionText}
                    </StyledTableCell>
                    <StyledTableCell scope="row" className={classes.answerText}>
                      {!surveyData.previousAnswers && surveyAnswer.answerText}
                      {!surveyAnswer.answerText && "Nema podataka"}
                      {surveyData.previousAnswers &&
                        surveyData.previousAnswers &&
                        surveyData.previousAnswers[i].surveyAnswers[j]
                          .answerText}
                    </StyledTableCell>
                    {surveyData.previousAnswers && (
                      <StyledTableCell
                        scope="row"
                        className={classes.answerText}
                      >
                        {surveyAnswer.changed &&
                          (surveyAnswer.answerText.length
                            ? surveyAnswer.answerText
                            : "(obrisano)")}
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))}
              </React.Fragment>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
